import { lazy, Suspense, useEffect, useRef, useState } from "react";
import axios from "axios";
import { RotateCcw, Plus, Minus, MapPin, Anchor, Plane, Trees, Drama, TowerControl } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { ZONES, LANDMARKS, zoneOf, diameterKm } from "@/lib/aktauData";
import { scrollToId } from "@/lib/scroll";
import { Reveal, SectionLabel } from "./Reveal";
import { AddressSearch } from "./AddressSearch";

const AktauLeaflet = lazy(() => import("./AktauLeaflet"));
const LM_ICONS = { anchor: Anchor, plane: Plane, trees: Trees, drama: Drama, lighthouse: TowerControl };
const btn = "inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-[var(--text)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors duration-300";
const sel = "rounded-full border border-white/15 bg-[#0F131C] px-4 py-2 text-sm text-[var(--text)] focus:border-[var(--gold)] outline-none";
const card = "rounded-3xl border border-white/10 bg-[#0F131C]/70 p-6";

const Toolbar = ({ n, api, zone, setZone, md, pickMd, mds }) => (
  <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="netmap-toolbar">
    <button className={btn} onClick={() => { setZone("all"); pickMd(""); api.current?.reset(); }} data-testid="netmap-reset-button"><RotateCcw className="w-4 h-4" />{n.reset}</button>
    <button className={btn} onClick={() => api.current?.zoomIn()} aria-label={n.zoomIn} data-testid="netmap-zoom-in-button"><Plus className="w-4 h-4" /><span className="hidden sm:inline">{n.zoomIn}</span></button>
    <button className={btn} onClick={() => api.current?.zoomOut()} aria-label={n.zoomOut} data-testid="netmap-zoom-out-button"><Minus className="w-4 h-4" /><span className="hidden sm:inline">{n.zoomOut}</span></button>
    <div className="flex flex-wrap gap-2 w-full sm:w-auto sm:ml-auto">
      <select className={`${sel} flex-1 sm:flex-none`} value={zone} onChange={(e) => setZone(e.target.value)} data-testid="netmap-zone-filter">
        <option value="all">{n.allZones}</option>
        {Object.keys(ZONES).map((z) => <option key={z} value={z}>{n.zones[z][0]}</option>)}
      </select>
      <select className={`${sel} flex-1 sm:flex-none`} value={md} onChange={(e) => pickMd(e.target.value)} data-testid="netmap-md-select">
        <option value="">{n.pickMd}</option>
        {mds.map((m) => <option key={m.num} value={m.num}>{m.num} {n.md}</option>)}
      </select>
    </div>
  </div>
);

const Legend = ({ n }) => (
  <div className={card} data-testid="netmap-legend">
    <h3 className="font-display text-lg text-[var(--text)]">{n.legend}</h3>
    <div className="mt-5 space-y-4">
      {Object.entries(ZONES).map(([z, c]) => (
        <div key={z} className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-full border-2 shrink-0" style={{ background: c.fill, borderColor: c.border === "#0A0D14" ? "rgba(255,255,255,.25)" : c.border }} />
          <span><span className="block text-sm text-[var(--text)]">{n.zones[z][0]}</span><span className="block text-xs text-[var(--muted)]">{n.zones[z][1]}</span></span>
        </div>
      ))}
    </div>
  </div>
);

const Hubs = ({ n, lang, hubs, onPick }) => (
  <div className={card} data-testid="netmap-hub-list">
    <h3 className="font-display text-lg text-[var(--text)]">{n.hubsTitle}</h3>
    <div className="mt-4 flex gap-4 text-xs text-[var(--muted)]">
      <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[var(--gold)] shadow-[0_0_10px_var(--gold)]" />{n.active}</span>
      <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full border border-[var(--gold)]" />{n.planned}</span>
    </div>
    <div className="mt-4 space-y-2">
      {hubs.map((h, i) => (
        <button key={h.id} onClick={() => onPick(h.id)} data-testid={`netmap-hub-${i}`} className="w-full text-left rounded-2xl border border-white/10 hover:border-[var(--gold)]/60 px-4 py-2.5 flex items-center gap-3 transition-colors duration-300">
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${h.status === "active" ? "bg-[var(--gold)]" : "border border-[var(--gold)]"}`} />
          <span className="min-w-0 flex-1"><span className="block text-sm text-[var(--text)] truncate">{lang === "kz" ? h.name_kz : h.name_ru}</span><span className="block text-xs text-[var(--muted)]">{n.radius} {diameterKm(h.radius_m)} {n.m}</span></span>
          <MapPin className="w-4 h-4 text-[var(--gold)] shrink-0" />
        </button>
      ))}
    </div>
    {hubs.some((h) => h.example) && <p className="mt-4 text-xs text-[var(--muted)] leading-relaxed" data-testid="netmap-example-note">{n.example}</p>}
  </div>
);

const Landmarks = ({ n }) => (
  <div className={card} data-testid="netmap-landmarks">
    <h3 className="font-display text-lg text-[var(--text)]">{n.landmarksTitle}</h3>
    <div className="mt-5 space-y-3">
      {LANDMARKS.map((l) => {
        const Icon = LM_ICONS[l.icon];
        return (
          <p key={l.key} className="flex items-start gap-3 text-sm">
            <Icon className="w-4 h-4 mt-0.5 text-[var(--gold)] shrink-0" />
            <span><span className="text-[var(--text)]">{n.lm[l.key][0]}</span> <span className="text-[var(--muted)]">— {n.lm[l.key][1]}</span></span>
          </p>
        );
      })}
    </div>
  </div>
);

export const NetworkMap = () => {
  const { t, lang } = useLang();
  const n = t.netmap;
  const api = useRef(null);
  const [hubs, setHubs] = useState([]);
  const [mds, setMds] = useState([]);
  const [zone, setZone] = useState("all");
  const [md, setMd] = useState("");
  const [found, setFound] = useState(null);

  const onFound = (p) => {
    setFound(p);
    if (window.innerWidth < 1024) scrollToId("netmap-frame");
    setTimeout(() => api.current?.focusPoint(p.lat, p.lng, 16), 50);
  };

  useEffect(() => {
    const api = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${api}/api/hubs`)
      .then((r) => setHubs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setHubs([]));
    axios.get(`${api}/api/microdistricts`)
      .then((r) => setMds(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMds([]));
  }, []);

  const pickMd = (num) => {
    setMd(num);
    if (!num) return;
    if (zone !== "all" && zone !== zoneOf(num)) setZone("all");
    setTimeout(() => api.current?.focusMd(num), 50);
  };

  const pickHub = (id) => {
    if (window.innerWidth < 1024) scrollToId("netmap-frame");
    api.current?.focusHub(id);
  };

  return (
    <section id="network-map" className="relative py-28 md:py-36" data-testid="netmap-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <SectionLabel n="✦">{n.label}</SectionLabel>
        <Reveal><h2 className="font-display text-3xl md:text-5xl mt-8 text-[var(--text)] max-w-4xl leading-[1.08]" data-testid="netmap-title">{n.title}</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-6 text-base md:text-lg text-[var(--muted)] max-w-2xl">{n.sub}</p></Reveal>

        <div className="mt-12">
          <AddressSearch s={n.search} hubs={hubs} lang={lang} onFound={onFound} />
          <Toolbar n={n} api={api} zone={zone} setZone={setZone} md={md} pickMd={pickMd} mds={mds} />
          <div id="netmap-frame" className="relative z-0 rounded-[28px] overflow-hidden border border-[var(--gold)]/30 h-[460px] md:h-[620px] bg-[#0F131C]" data-testid="netmap-map">
            <Suspense fallback={null}><AktauLeaflet ref={api} hubs={hubs} mds={mds} zone={zone} n={n} lang={lang} draft={found} /></Suspense>
          </div>
          <p className="mt-2 text-right text-xs text-[var(--muted)]" data-testid="netmap-attribution">
            {n.osm} <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--gold)]">OpenStreetMap</a>
          </p>
        </div>

        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Legend n={n} />
          <Hubs n={n} lang={lang} hubs={hubs} onPick={pickHub} />
          <Landmarks n={n} />
        </div>
      </div>
    </section>
  );
};
